import React, { useEffect, useState } from 'react';
import { LeaseWithDetails, CreateLeaseRequest, UpdateLeaseRequest } from './types';
import LeaseTermsSection from './LeaseTermsSection';
import LeaseUnitSection from './LeaseUnitSection';
import LeaseTenantsSection from './LeaseTenantsSection';
import { LeaseUnitOption } from './types';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import { ActionButton } from '../../../components/common/ActionButton';
import '../../../components/common/CRMForm.css';

interface LeaseFormProps {
  lease: LeaseWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  createLease: (data: CreateLeaseRequest) => Promise<LeaseWithDetails>;
  updateLease: (id: number, data: UpdateLeaseRequest) => Promise<LeaseWithDetails>;
  endLease: (id: number, data?: { endDate?: string }) => Promise<LeaseWithDetails>;
  deleteLease: (id: number) => Promise<void>;
}

interface FormErrors {
  typeId?: string;
  beginDate?: string;
  endDate?: string;
  renewalDate?: string;
  leaseAmount?: string;
  unit?: string;
  tenants?: string;
}

const LeaseForm: React.FC<LeaseFormProps> = ({
  lease,
  onSave,
  onCancel,
  onDirtyChange,
  createLease,
  updateLease,
  endLease,
  deleteLease
}) => {
  const isEditing = !!lease;
  const leaseId = lease?.id ?? null;

  const [typeId, setTypeId] = useState(0);
  const [beginDate, setBeginDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [leaseAmount, setLeaseAmount] = useState(0);
  const [securityAmount, setSecurityAmount] = useState(0);
  const [selectedUnit, setSelectedUnit] = useState<LeaseUnitOption | null>(null);
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
  const [renewalManuallyCleared, setRenewalManuallyCleared] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (lease) {
      setTypeId(lease.typeId);
      setBeginDate(lease.beginDate);
      setEndDate(lease.endDate);
      setRenewalDate(lease.renewalDate || '');
      setLeaseAmount(lease.leaseAmount);
      setSecurityAmount(lease.securityAmount || 0);
      setSelectedTenantIds(lease.tenants.map((t) => t.tenantId));
      setRenewalManuallyCleared(!lease.renewalDate);

      if (lease.propertiesAddressesId) {
        setSelectedUnit({
          propertiesAddressesId: lease.propertiesAddressesId,
          propertyId: lease.propertyId || 0,
          propertyName: lease.propertyName || '',
          propertyDescription: lease.propertyDescription,
          unitAddressLine: lease.unitAddressLine || '',
          categoryName: lease.categoryName
        });
      } else {
        setSelectedUnit(null);
      }
    } else {
      setTypeId(0);
      setBeginDate('');
      setEndDate('');
      setRenewalDate('');
      setLeaseAmount(0);
      setSecurityAmount(0);
      setSelectedUnit(null);
      setSelectedTenantIds([]);
      setRenewalManuallyCleared(false);
    }
    setHasChanges(false);
    setErrors({});
  }, [leaseId]);

  const markDirty = () => setHasChanges(true);

  const { confirmNavigation } = useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!typeId) newErrors.typeId = 'Lease type is required';
    if (!beginDate) newErrors.beginDate = 'Start date is required';
    if (!endDate) newErrors.endDate = 'End date is required';
    if (!selectedUnit) newErrors.unit = 'Unit is required';
    if (selectedTenantIds.length < 1) newErrors.tenants = 'At least one tenant is required';
    if (selectedTenantIds.length > 10) newErrors.tenants = 'At most 10 tenants are allowed';
    if (leaseAmount < 0.01) newErrors.leaseAmount = 'Lease amount must be at least $0.01';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (): CreateLeaseRequest => ({
    typeId,
    propertiesAddressesId: selectedUnit!.propertiesAddressesId,
    beginDate,
    endDate,
    renewalDate: renewalDate || null,
    leaseAmount,
    securityAmount: securityAmount > 0 ? securityAmount : null,
    tenantIds: selectedTenantIds
  });

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEditing && lease) {
        const updateData: UpdateLeaseRequest = buildPayload();
        await updateLease(lease.id, updateData);
      } else {
        await createLease(buildPayload());
      }
      setHasChanges(false);
      onSave();
    } catch (err) {
      console.error('Error saving lease:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEndLease = async () => {
    if (!lease) return;
    if (!window.confirm('End this lease? The end date will be confirmed on the lease record.')) {
      return;
    }

    setLoading(true);
    try {
      await endLease(lease.id, { endDate });
      setHasChanges(false);
      onSave();
    } catch (err) {
      console.error('Error ending lease:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!lease) return;
    if (!window.confirm('Delete this lease? This removes tenant associations and hides the lease from the list.')) {
      return;
    }

    setLoading(true);
    try {
      await deleteLease(lease.id);
      setHasChanges(false);
      onSave();
    } catch (err) {
      console.error('Error deleting lease:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!confirmNavigation()) {
      return;
    }
    onCancel();
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Lease' : 'Create Lease'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      <div className="form-content">
        <LeaseTermsSection
          typeId={typeId}
          beginDate={beginDate}
          endDate={endDate}
          renewalDate={renewalDate}
          leaseAmount={leaseAmount}
          securityAmount={securityAmount}
          isCreate={!isEditing}
          renewalManuallyCleared={renewalManuallyCleared}
          errors={errors}
          onTypeChange={(id) => { setTypeId(id); markDirty(); }}
          onBeginDateChange={(date) => { setBeginDate(date); markDirty(); }}
          onEndDateChange={(date) => { setEndDate(date); markDirty(); }}
          onRenewalDateChange={(date) => { setRenewalDate(date); markDirty(); }}
          onRenewalManuallyCleared={setRenewalManuallyCleared}
          onLeaseAmountChange={(amount) => { setLeaseAmount(amount); markDirty(); }}
          onSecurityAmountChange={(amount) => { setSecurityAmount(amount); markDirty(); }}
        />

        <LeaseUnitSection
          selectedUnit={selectedUnit}
          onUnitSelect={(unit) => { setSelectedUnit(unit); markDirty(); }}
          includeLeaseId={lease?.id}
          error={errors.unit}
        />

        <LeaseTenantsSection
          selectedTenantIds={selectedTenantIds}
          onTenantsChange={(ids) => { setSelectedTenantIds(ids); markDirty(); }}
          includeLeaseId={lease?.id}
          error={errors.tenants}
        />
      </div>

      <div className="app-form-actions">
        {isEditing && (
          <button
            type="button"
            className="app-btn app-btn-danger"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </button>
        )}
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        {isEditing && (
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={handleEndLease}
            disabled={loading}
          >
            End
          </button>
        )}
        <ActionButton
          className="app-btn app-btn-primary"
          label={isEditing ? 'Update' : 'Create'}
          busyLabel="Saving..."
          busy={loading}
          disabled={!hasChanges}
          onClick={() => handleSave()}
        />
      </div>
    </div>
  );
};

export default LeaseForm;
